import { defineStore } from 'pinia';
import { useBibleStore } from './bible';

// Chronological Book Order (Approximation of standard historical order)
const CHRONOLOGICAL_ORDER = [
    "Genesis", "Job", "Exodus", "Leviticus", "Numbers", "Deuteronomy",
    "Joshua", "Judges", "Ruth", "1 Samuel", "2 Samuel", "1 Chronicles", "Psalms",
    "2 Chronicles", "Song of Solomon", "Proverbs", "Ecclesiastes", "1 Kings", "2 Kings",
    "Jonah", "Amos", "Hosea", "Micah", "Isaiah", "Nahum", "Zephaniah", "Jeremiah",
    "Lamentations", "Habakkuk", "Daniel", "Ezekiel", "Obadiah", "Haggai", "Zechariah",
    "Esther", "Ezra", "Nehemiah", "Malachi",
    // NT (Canonical/Standard-ish)
    "Matthew", "Mark", "Luke", "John", "Acts",
    "James", "Galatians", "1 Thessalonians", "2 Thessalonians", "1 Corinthians", "2 Corinthians",
    "Romans", "Ephesians", "Philippians", "Colossians", "Philemon", "1 Timothy", "Titus",
    "1 Peter", "2 Timothy", "2 Peter", "Hebrews", "Jude", "1 John", "2 John", "3 John", "Revelation"
];

export const usePlanStore = defineStore('plan', {
    state: () => ({
        predefinedPlans: [],
        currentPlan: null
    }),
    actions: {
        initPredefinedPlans() {
            this.predefinedPlans = [
                {
                    "id": "90-day-new-testament-intensive_992e0f50e4fb4a21",
                    "name": "90-Day New Testament (Intensive)",
                    "description": "An intensive 90-day plan through the New Testament.",
                    "timeCommitment": "20+",
                    "focusTags": ["New Testament Only"],
                    "paceTag": "Challenging",
                    "durationDays": 90
                },
                {
                    "id": "6-month-chronological-whole-bible_2aa22773dc3d2ad7",
                    "name": "6-Month Chronological Whole Bible",
                    "description": "Read the entire Bible in about six months in chronological order.",
                    "timeCommitment": "20+",
                    "focusTags": ["Whole Bible", "Chronological Order"],
                    "paceTag": "Challenging",
                    "durationDays": 182
                },
                {
                    "id": "6-month-whole-bible-snapshot_01150767a7773401",
                    "name": "6-Month Whole Bible Snapshot",
                    "description": "Fast-paced overview of the whole Bible in six months.",
                    "timeCommitment": "20+",
                    "focusTags": ["Whole Bible"],
                    "paceTag": "Challenging",
                    "durationDays": 182
                },
                {
                    "id": "1-year-gospels-acts_776eef697fd79647",
                    "name": "1-Year Gospels & Acts",
                    "description": "Follow the life of Christ and the early church over a year.",
                    "timeCommitment": "5-10",
                    "focusTags": ["New Testament Only"],
                    "paceTag": "Flexible",
                    "durationDays": 365
                },
                {
                    "id": "1-year-high-intensity-epistles_04563ade927e5a65",
                    "name": "1-Year High-Intensity Epistles",
                    "description": "Deep dive into the New Testament epistles over one year.",
                    "timeCommitment": "10-20",
                    "focusTags": ["New Testament Only", "Deep Study"],
                    "paceTag": "Challenging",
                    "durationDays": 365
                },
                {
                    "id": "1-year-ot-history-nt-survey_a4f3caef5c2eccb5",
                    "name": "1-Year OT History & NT Survey",
                    "description": "Focus on OT historical books and the entire New Testament over one year.",
                    "timeCommitment": "10-20",
                    "focusTags": ["Old Testament Only", "New Testament Only"],
                    "paceTag": "Challenging",
                    "durationDays": 365
                },
                {
                    "id": "1-year-prophets_6ce02f8422feb34a",
                    "name": "1-Year Prophets",
                    "description": "Spend a year reading through the Major and Minor Prophets.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Old Testament Only", "Deep Study"],
                    "paceTag": "Flexible",
                    "durationDays": 365
                },
                {
                    "id": "1-year-psalms-twice-proverbs-monthly_4d22f8022c4db548",
                    "name": "1-Year Psalms (Twice) & Proverbs (Monthly)",
                    "description": "Patterned plan focusing on Psalms and Proverbs throughout the year.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Deep Study"],
                    "paceTag": "Flexible",
                    "durationDays": 365
                },
                {
                    "id": "1-year-whole-bible-weekday-focus_8b2734ecead90ea5",
                    "name": "1-Year Whole Bible (Weekday Focus)",
                    "description": "Whole Bible in one year with a plan intended for weekday reading (schedule type flagged for future logic).",
                    "timeCommitment": "10-20",
                    "focusTags": ["Whole Bible"],
                    "paceTag": "Challenging",
                    "durationDays": 365
                },
                {
                    "id": "1-year-wisdom-books_80973dc96443e240",
                    "name": "1-Year Wisdom Books",
                    "description": "Slow meditation through Job, Psalms, Proverbs, Ecclesiastes, and Song of Solomon over a year.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Deep Study"],
                    "paceTag": "Flexible",
                    "durationDays": 365
                },
                {
                    "id": "one-year-balanced-ot-nt_15f428a04e9bc848",
                    "name": "One-Year Balanced OT/NT",
                    "description": "Whole Bible in one year with a conceptually balanced OT/NT load.",
                    "timeCommitment": "10-20",
                    "focusTags": ["Whole Bible"],
                    "paceTag": "Challenging",
                    "durationDays": 365
                },
                {
                    "id": "one-year-chronological-simple_2673724ff7f3864d",
                    "name": "One-Year Chronological (Simple)",
                    "description": "Chronological-style reading of the whole Bible in one year.",
                    "timeCommitment": "10-20",
                    "focusTags": ["Whole Bible", "Chronological Order"],
                    "paceTag": "Challenging",
                    "durationDays": 365
                },
                {
                    "id": "one-year-gospels_4543d4079bce4be0",
                    "name": "One-Year Gospels",
                    "description": "Spend a year in the four Gospels.",
                    "timeCommitment": "5-10",
                    "focusTags": ["New Testament Only", "Deep Study"],
                    "paceTag": "Flexible",
                    "durationDays": 365
                },
                {
                    "id": "one-year-new-testament_9635bf9b99ba362f",
                    "name": "One-Year New Testament",
                    "description": "Read the New Testament slowly over one year.",
                    "timeCommitment": "5-10",
                    "focusTags": ["New Testament Only"],
                    "paceTag": "Flexible",
                    "durationDays": 365
                },
                {
                    "id": "one-year-psalms-proverbs_892b4a35e48870bd",
                    "name": "One-Year Psalms & Proverbs",
                    "description": "Focus on Psalms and Proverbs over a year for daily wisdom.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Deep Study"],
                    "paceTag": "Flexible",
                    "durationDays": 365
                },
                {
                    "id": "one-year-whole-bible-canonical_ef61c041dff3537c",
                    "name": "One-Year Whole Bible (Canonical)",
                    "description": "Read the entire Bible in one year in canonical order (Genesis to Revelation).",
                    "timeCommitment": "10-20",
                    "focusTags": ["Whole Bible"],
                    "paceTag": "Challenging",
                    "durationDays": 365
                },
                {
                    "id": "2-year-morning-evening-easy_7ce5abaa083e86a2",
                    "name": "2-Year Morning & Evening (Easy)",
                    "description": "Gentle two-year plan through the whole Bible, suitable for morning and evening readings.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Whole Bible"],
                    "paceTag": "Flexible",
                    "durationDays": 730
                },
                {
                    "id": "two-year-whole-bible-canonical_fdb7b12dd7ec3cb0",
                    "name": "Two-Year Whole Bible (Canonical)",
                    "description": "Read the entire Bible over two years at a gentler pace.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Whole Bible"],
                    "paceTag": "Flexible",
                    "durationDays": 730
                },
                {
                    "id": "3-year-law-history-nt_ed3a383c0fd00c16",
                    "name": "3-Year Law, History & NT",
                    "description": "Three-year plan: two years in OT Law & History, one year in the New Testament.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Whole Bible"],
                    "paceTag": "Flexible",
                    "durationDays": 1095
                },
                {
                    "id": "three-year-old-testament_0db04dd9b973eb2c",
                    "name": "Three-Year Old Testament",
                    "description": "Cover the Old Testament in three years with roughly one chapter per day.",
                    "timeCommitment": "5-10",
                    "focusTags": ["Old Testament Only"],
                    "paceTag": "Flexible",
                    "durationDays": 1095
                }
            ];
        },
        // --- CORE LOGIC ---

        async generatePlanFromDefinition(def, bibleData) {
            if (!bibleData || !bibleData.books) throw new Error("Bible data is not loaded.");

            // 1. Determine Scope (list of chapters to read)
            let chaptersToRead = [];
            const books = bibleData.books;

            // Book Group Definitions
            const groups = {
                pentateuch: ["Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy"],
                history_ot: ["Joshua", "Judges", "Ruth", "1 Samuel", "2 Samuel", "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles", "Ezra", "Nehemiah", "Esther"],
                wisdom: ["Job", "Psalms", "Proverbs", "Ecclesiastes", "Song of Solomon"],
                major_prophets: ["Isaiah", "Jeremiah", "Lamentations", "Ezekiel", "Daniel"],
                minor_prophets: ["Hosea", "Joel", "Amos", "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai", "Zechariah", "Malachi"],
                gospels: ["Matthew", "Mark", "Luke", "John"],
                history_nt: ["Acts"],
                pauline_epistles: ["Romans", "1 Corinthians", "2 Corinthians", "Galatians", "Ephesians", "Philippians", "Colossians", "1 Thessalonians", "2 Thessalonians", "1 Timothy", "2 Timothy", "Titus", "Philemon"],
                general_epistles: ["Hebrews", "James", "1 Peter", "2 Peter", "1 John", "2 John", "3 John", "Jude"],
                revelation: ["Revelation"]
            };

            if (def.scope.type === 'whole_bible') {
                chaptersToRead = this.buildAllChaptersList(books);
            } else if (def.scope.type === 'ot') {
                const otBooks = books.filter(b => b.testament === 'Old');
                chaptersToRead = this.buildAllChaptersList(otBooks);
            } else if (def.scope.type === 'nt') {
                const ntBooks = books.filter(b => b.testament === 'New');
                chaptersToRead = this.buildAllChaptersList(ntBooks);
            } else if (groups[def.scope.type]) {
                // Handle predefined groups
                const targetBooks = groups[def.scope.type];
                const selected = books.filter(b => targetBooks.includes(b.name));
                chaptersToRead = this.buildAllChaptersList(selected);
            } else if (def.scope.type === 'custom_books') {
                const allowed = def.scope.bookNames || [];
                const customBooks = books.filter(b => allowed.includes(b.name));
                chaptersToRead = this.buildAllChaptersList(customBooks);
            }

            // 2. Apply Order (Canonical vs Chronological)
            if (def.order === 'chronological') {
                chaptersToRead.sort((a, b) => {
                    const idxA = CHRONOLOGICAL_ORDER.indexOf(a.book);
                    const idxB = CHRONOLOGICAL_ORDER.indexOf(b.book);

                    // If book not found in list (fallback), put at end
                    const sortA = idxA === -1 ? 999 : idxA;
                    const sortB = idxB === -1 ? 999 : idxB;

                    if (sortA !== sortB) return sortA - sortB;
                    // Same book, sort by chapter
                    return a.chapter - b.chapter;
                });
            }

            // 3. Distribute Readings
            const loadBalance = def.loadBalance || 'chapters';

            // Adjust loop duration for 'weekdays' schedule to insure the plan actually finishes in the requested calendar duration
            // e.g. 365 calendar days * 5/7 = 261 readings
            let distributionDays = def.durationDays;
            if (def.schedule && def.schedule.type === 'weekdays') {
                distributionDays = Math.floor(def.durationDays * (5 / 7));
            }
            // Ensure at least 1 day
            distributionDays = Math.max(1, distributionDays);

            const days = this.distributeChapters(chaptersToRead, distributionDays, loadBalance);

            // 4. Return Plan Structure
            // Note: ID management is now handled by the caller (Base64 encoding or Firestore ID)
            // Hashing has been removed.

            return {
                id: null,
                definition: def,
                days
            };
        },

        buildAllChaptersList(bookList) {
            const list = [];
            bookList.forEach(b => {
                b.verses_per_chapter.forEach((vCount, i) => {
                    list.push({
                        book: b.name,
                        abbr: b.abbreviation || b.name.substring(0, 3),
                        chapter: i + 1,
                        verses: vCount
                    });
                });
            });
            return list;
        },

        distributeChapters(readings, durationDays, method = 'chapters') {
            const days = [];
            const totalItems = readings.length;

            if (method === 'chapters') {
                // Original simple chapter distribution
                const itemsPerDay = Math.ceil(totalItems / durationDays);
                let currentIndex = 0;

                for (let d = 1; d <= durationDays; d++) {
                    if (currentIndex >= totalItems) break;
                    const sliceSize = itemsPerDay;
                    const end = Math.min(currentIndex + sliceSize, totalItems);
                    const dayReadings = readings.slice(currentIndex, end);
                    days.push({ day: d, readings: dayReadings });
                    currentIndex = end;
                }
            } else if (method === 'verses') {
                // Verse-based load balancing
                const totalVerses = readings.reduce((sum, r) => sum + r.verses, 0);
                const versesPerDay = totalVerses / durationDays;

                let currentReadingIdx = 0;
                let currentVerseOffset = 0; // 0-based offset into the current chapter
                let accumulatedVersesGlobal = 0; // Total verses assigned so far

                for (let d = 1; d <= durationDays; d++) {
                    if (currentReadingIdx >= totalItems) break;

                    const dayReadings = [];
                    // Target total accumulated verses by the end of this day
                    let targetGlobal = Math.round(d * versesPerDay);

                    // Allow small buffer to prevent tiny slivers at end unless necessary
                    // (Not strictly needed with Math.round)

                    while (currentReadingIdx < totalItems) {
                        const r = readings[currentReadingIdx];
                        const remainingInChapter = r.verses - currentVerseOffset;

                        // How many do we need to reach target?
                        const needed = targetGlobal - accumulatedVersesGlobal;

                        // Case 1: We need more than or equal to what's left in this chapter
                        // OR if we need very close to the full amount (avoid leaving 1-2 verses)
                        if (needed >= remainingInChapter || (remainingInChapter - needed) < 3) {
                            // Take entire remainder of chapter
                            const startV = currentVerseOffset + 1;
                            const endV = r.verses;

                            // Formatting
                            const versesStr = (startV === 1 && endV === r.verses)
                                ? '' // Whole chapter
                                : `${startV}-${endV}`;

                            dayReadings.push({
                                ...r,
                                verses: remainingInChapter, // For weight calculation
                                displayVerses: versesStr
                            });

                            accumulatedVersesGlobal += remainingInChapter;

                            // Move to next chapter
                            currentReadingIdx++;
                            currentVerseOffset = 0;

                            // If we exactly met or exceeded, stop for today? 
                            // Only stop if we met the target (approx)
                            if (accumulatedVersesGlobal >= targetGlobal) break;

                        } else {
                            // Case 2: We only need a slice of this chapter
                            // Be careful not to slice 1 verse if we can avoid it, but strict math for now
                            // Take 'needed' amount
                            // Ensure we take at least 1 verse if needed > 0
                            let take = Math.max(1, needed);

                            // Don't leave a tiny remainder for tomorrow? 
                            // Handled by check above (remaining - needed < 3)

                            const startV = currentVerseOffset + 1;
                            const endV = currentVerseOffset + take;

                            dayReadings.push({
                                ...r,
                                verses: take,
                                displayVerses: `${startV}-${endV}`
                            });

                            accumulatedVersesGlobal += take;
                            currentVerseOffset += take;

                            break; // Finished today's quota
                        }
                    }

                    if (dayReadings.length > 0) {
                        days.push({ day: d, readings: dayReadings });
                    }
                }
            }

            return days;
        },

        async setCurrentPlanById(id) {
            // Static file IDs are now longer (e.g. "one-year-chronological-simple_2673724ff7f3864d")
            // Base64 definitions are usually much longer than 50-60 chars for a full plan def.
            if (id.length < 100) {
                try {
                    const response = await fetch(`/plans/${id}.json`);
                    if (response.ok) {
                        const plan = await response.json();
                        // ENSURE ID consistency
                        plan.id = id;
                        this.currentPlan = plan;
                        return;
                    }
                } catch (e) {
                    console.warn(`Failed to load static plan for ID ${id}:`, e);
                }
            }

            let foundDef = null;
            try {
                const jsonString = atob(id);
                foundDef = JSON.parse(jsonString);
            } catch (e) {
                console.warn("ID is not a valid base64 definition nor a static file", id);
            }

            if (foundDef) {
                const bibleStore = useBibleStore();
                if (!bibleStore.bibleData) await bibleStore.loadBibleData();
                const generated = await this.generatePlanFromDefinition(foundDef, bibleStore.bibleData);
                // Ensure dynamic ID matches requested ID if it was just base64
                generated.id = id;
                this.currentPlan = generated;
            } else {
                this.currentPlan = null;
                console.error("Plan not found for ID:", id);
            }
        },


    }
});
